import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { MoreVertical, Trash2, UserCog, Mail, Bell, Users } from "lucide-react";
import { Profile, UserRole } from "@/pages/users/types";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { UserProfileDialog } from "@/pages/users/components/UserProfileDialog";
import { useEffect } from "react";

interface AdminTeamMemberCardProps {
  member: Profile;
  currentUserId: string;
  memberType: string; // 'parent_admin', 'contributor', 'manager'
  teamIds?: string[]; // Team IDs this member is assigned to
  onUpdate: () => void;
}

export const AdminTeamMemberCard = ({ member, currentUserId, memberType, teamIds = [], onUpdate }: AdminTeamMemberCardProps) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [showNotificationDialog, setShowNotificationDialog] = useState(false);
  const [showTeamAssignmentDialog, setShowTeamAssignmentDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [availableTeams, setAvailableTeams] = useState<Array<{ id: string; name: string }>>([]);
  const [assignedTeamIds, setAssignedTeamIds] = useState<string[]>(teamIds);
  const [teamNames, setTeamNames] = useState<string[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const { toast } = useToast();

  const currentRole = member.user_roles?.[0]?.role || "guard";
  const isCurrentUser = member.id === currentUserId;
  const isManager = currentRole === "manager";

  // Fetch team names for display on card
  useEffect(() => {
    const fetchTeamNames = async () => {
      if (teamIds.length === 0 || !isManager) {
        setTeamNames([]);
        return;
      }
      
      try {
        const { data, error } = await supabase
          .from("teams")
          .select("name")
          .in("id", teamIds);
        
        if (error) throw error;
        setTeamNames(data?.map(t => t.name) || []);
      } catch (error) {
        console.error("Error fetching team names:", error);
      }
    };

    fetchTeamNames();
  }, [teamIds.join(','), isManager]);

  useEffect(() => {
    if (showTeamAssignmentDialog && isManager) {
      fetchTeamsAndAssignments();
    }
  }, [showTeamAssignmentDialog, isManager]);

  const fetchTeamsAndAssignments = async () => {
    setLoadingTeams(true);
    try {
      // Get current user to check admin access
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch all teams accessible to the current admin
      const { data: teams, error: teamsError } = await supabase
        .from("teams")
        .select("id, name")
        .eq("created_by", user.id)
        .eq("status", "active")
        .order("name");

      if (teamsError) throw teamsError;
      setAvailableTeams(teams || []);

      // Fetch current team assignments for this manager
      const { data: assignments, error: assignError } = await supabase
        .from("manager_teams")
        .select("team_id")
        .eq("manager_id", member.id);

      if (assignError) throw assignError;
      setAssignedTeamIds(assignments?.map(a => a.team_id) || []);
    } catch (error: any) {
      console.error("Error fetching teams:", error);
      toast({
        title: "Error",
        description: "Failed to load teams",
        variant: "destructive",
      });
    } finally {
      setLoadingTeams(false);
    }
  };

  const handleTeamToggle = async (teamId: string, isAssigned: boolean) => {
    try {
      if (isAssigned) {
        // Assign team to manager
        const { error } = await supabase
          .from("manager_teams")
          .insert({ manager_id: member.id, team_id: teamId });

        if (error) throw error;
        
        setAssignedTeamIds([...assignedTeamIds, teamId]);
        
        // Update team names locally
        const team = availableTeams.find(t => t.id === teamId);
        if (team) {
          setTeamNames([...teamNames, team.name]);
        }
        
        toast({
          title: "Team assigned",
          description: "Manager has been assigned to the team",
        });
      } else {
        // Remove team assignment
        const { error } = await supabase
          .from("manager_teams")
          .delete()
          .eq("manager_id", member.id)
          .eq("team_id", teamId);

        if (error) throw error;
        
        setAssignedTeamIds(assignedTeamIds.filter(id => id !== teamId));
        
        // Update team names locally
        const team = availableTeams.find(t => t.id === teamId);
        if (team) {
          setTeamNames(teamNames.filter(name => name !== team.name));
        }
        
        toast({
          title: "Team unassigned",
          description: "Manager has been removed from the team",
        });
      }
    } catch (error: any) {
      console.error("Error updating team assignment:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update team assignment",
        variant: "destructive",
      });
    }
  };

  const getInitials = (name: string) => {
    const names = name.split(" ");
    return names.map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getMemberTypeLabel = (type: string) => {
    switch (type) {
      case "parent_admin":
        return "Parent Admin";
      case "contributor":
        return "Contributor";
      case "manager":
        return "Manager";
      default:
        return type;
    }
  };

  const getMemberTypeBadgeVariant = (type: string) => {
    switch (type) {
      case "parent_admin":
        return "default";
      case "contributor":
        return "secondary";
      case "manager":
        return "outline";
      default:
        return "outline";
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case "super_admin":
        return "default";
      case "admin":
        return "secondary";
      case "manager":
        return "outline";
      default:
        return "outline";
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case "super_admin":
        return "Super Admin";
      case "admin":
        return "Admin";
      case "manager":
        return "Manager";
      case "guard":
        return "User";
      case "client":
        return "Client";
      default:
        return role;
    }
  };

  const handleDeleteMember = async () => {
    if (isCurrentUser) {
      toast({
        title: "Cannot delete yourself",
        description: "You cannot delete your own account",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsDeleting(true);

      // Delete the user using the delete-client edge function
      const { error } = await supabase.functions.invoke("delete-client", {
        body: { clientId: member.id },
      });

      if (error) throw error;

      toast({
        title: "Member deleted",
        description: `${member.full_name} has been removed from the team`,
      });

      onUpdate();
    } catch (error: any) {
      console.error("Error deleting member:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete team member",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleRoleChange = async (newRole: UserRole) => {
    if (isCurrentUser) {
      toast({
        title: "Cannot change your own role",
        description: "You cannot modify your own role for security reasons",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsUpdating(true);
      setSelectedRole(newRole);

      // Delete existing role
      const { error: deleteError } = await supabase
        .from("user_roles")
        .delete()
        .eq("user_id", member.id);

      if (deleteError) throw deleteError;

      // Insert new role
      const { error: insertError } = await supabase
        .from("user_roles")
        .insert({ user_id: member.id, role: newRole });

      if (insertError) throw insertError;

      toast({
        title: "Role updated",
        description: `${member.full_name}'s role has been updated to ${getRoleLabel(newRole)}`,
      });

      onUpdate();
    } catch (error: any) {
      console.error("Error updating role:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update role",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
      setSelectedRole(null);
      setShowRoleDialog(false);
    }
  };

  return (
    <>
      <Card className="group hover:shadow-lg transition-all duration-200 border-border/50 hover:border-primary/20">
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Avatar className="h-12 w-12 border-2 border-primary/10">
                <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/10 text-primary font-semibold">
                  {getInitials(member.full_name || member.email || "?")}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-base truncate">{member.full_name}</h3>
                <p className="text-sm text-muted-foreground truncate flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {member.email}
                </p>
              </div>
            </div>
            
            {!isCurrentUser && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowRoleDialog(true)}>
                    <UserCog className="h-4 w-4 mr-2" />
                    Change Role
                  </DropdownMenuItem>
                  {isManager && (
                    <DropdownMenuItem onClick={() => setShowTeamAssignmentDialog(true)}>
                      <Users className="h-4 w-4 mr-2" />
                      Assign Teams
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setShowDeleteDialog(true)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Member
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {isCurrentUser && (
              <Badge variant="outline" className="ml-2">
                You
              </Badge>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant={getMemberTypeBadgeVariant(memberType)} className="text-xs">
                {getMemberTypeLabel(memberType)}
              </Badge>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8"
              onClick={() => setShowNotificationDialog(true)}
            >
              <Bell className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>

          {/* Display assigned teams for managers */}
          {isManager && teamNames.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border/50">
              <div className="flex items-start gap-2">
                <Users className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex flex-wrap gap-1">
                  {teamNames.map((name, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {name}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Notification Preferences Dialog */}
      <UserProfileDialog
        profile={member}
        open={showNotificationDialog}
        onOpenChange={setShowNotificationDialog}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Team Member?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete <strong>{member.full_name}</strong>? 
              This action will remove their account, login credentials, and all associated data. 
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteMember}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete Member"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change Role Dialog */}
      <AlertDialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Role for {member.full_name}</AlertDialogTitle>
            <AlertDialogDescription>
              Select a new role for this team member. Current role: <strong>{getRoleLabel(currentRole)}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-4">
            {(["manager", "admin", "guard", "client"] as UserRole[]).map((role) => (
              <Button
                key={role}
                variant={selectedRole === role ? "default" : "outline"}
                className="w-full justify-start"
                onClick={() => handleRoleChange(role)}
                disabled={isUpdating || role === currentRole}
              >
                <UserCog className="h-4 w-4 mr-2" />
                {getRoleLabel(role)}
                {role === currentRole && <Badge variant="secondary" className="ml-auto text-xs">Current</Badge>}
              </Button>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUpdating}>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Team Assignment Dialog */}
      <Dialog open={showTeamAssignmentDialog} onOpenChange={setShowTeamAssignmentDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Teams to {member.full_name}</DialogTitle>
            <DialogDescription>
              Select which teams this manager should have access to
            </DialogDescription>
          </DialogHeader>
          {loadingTeams ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : availableTeams.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No teams available
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto py-4">
              {availableTeams.map((team) => {
                const isAssigned = assignedTeamIds.includes(team.id);
                return (
                  <div
                    key={team.id}
                    className="flex items-center space-x-2 p-3 rounded-lg hover:bg-muted/50"
                  >
                    <Checkbox
                      id={`team-${team.id}`}
                      checked={isAssigned}
                      onCheckedChange={(checked) => handleTeamToggle(team.id, checked as boolean)}
                    />
                    <label 
                      htmlFor={`team-${team.id}`}
                      className="flex-1 cursor-pointer font-medium"
                    >
                      {team.name}
                    </label>
                    {isAssigned && (
                      <Badge variant="secondary" className="text-xs">
                        Assigned
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
